import React, { useState, useRef, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Stack,
  Fade,
  Grow,
  LinearProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Mic, Square, Send, ChevronLeft, Volume2, CheckCircle2, RefreshCcw, Sparkles, Trash2, Pause, Play, UserCircle } from "lucide-react";
import axiosInstance from "../../../services/axiosInterceptors";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import AutohideSnackbar from "../../../components/AutohideSnackbar";
import { theme as colorTheme } from "../../../Convoze.theme";
import ParentWrapper from "../../../components/ParentWrapper";
import Header from "../../../components/header";
import { useNavigate } from "react-router-dom";

const primary = colorTheme('palette', 'primary').value;

const VoiceCapture = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0: Setup, 1: Recording, 2: Review
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [wpm, setWpm] = useState(140);
  const [hrs, setHrs] = useState(["Default HR", "Selection A", "Selection B"]);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [playbackHighlightedIndex, setPlaybackHighlightedIndex] = useState(-1);
  const recognitionRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const wordRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      userName: "",
      dob: "",
      email: "",
      mobile: "",
      hrNameSelect: "",
      hrNameNew: "",
      paragraph: `Dreadful Experience
Enjoying a cup of tea while sitting on my balcony on a Sunday morning, suddenly, I felt a deep pain in my chest. I thought that this was the last moment of my life. I wanted to call my daughter, my husband and everybody to say the final Good Bye, but couldn’t do so. I was thinking of their life without me. I was just worried what would happen to them if I collapsed. I just prayed, “O God! Give me some more time.” I thought about my daughter who was in the first year of her college life I had always thought of giving her a wonderful life in which she had all her dreams fulfilled. Then my thoughts shifted towards my husband who wouldn’t be able to lead his life without me. He was so used to me around him all the time. Suddenly, a thought came to my mind ‘You have to live. You can’t die like this. Get up. Do something,’ I tried to call my family again but it was as if I was dumb. No words came out of my mouth. I knew 1 should not lose my wits. I must do something to keep going until my family noticed me. Suddenly, my eyes fell on the mobile phone on the table nearby. I picked it up and dialed 102.1 don’t remember what happened after that when I awoke I found myself in a hospital room, with a team of doctors bending over me and watching me. Later, I was told that I had been carried in an ambulance to a multi-specialty hospital at the nick of time. Delay would have been fatal for me. I was happy. I could still see this wonderful world. I immediately decided to give my best to my family as life is short-lived.`
    },
    validationSchema: Yup.object({
      userName: Yup.string().required("Candidate Name is required"),
      dob: Yup.date().nullable().required("Date of Birth is required"),
      email: Yup.string().email("Invalid email address").required("Email Address is required"),
      mobile: Yup.string().matches(/^[0-9]+$/, "Must be only digits").min(10, "Minimum 10 digits").required("Mobile Number is required"),
      hrNameSelect: Yup.string().when('hrNameNew', {
        is: (hrNameNew) => !hrNameNew || hrNameNew.trim().length === 0,
        then: () => Yup.string().required("HR Name is required"),
        otherwise: () => Yup.string().notRequired(),
      }),
      hrNameNew: Yup.string(),
      paragraph: Yup.string().required("Paragraph text is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const token = localStorage.getItem("token");
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const formattedDate = values.dob ? new Date(values.dob).toISOString().split('T')[0] : "";

        // await axiosInstance.post(apiEndPointsConfig.candidateDetail, {
        //   userName: values.userName || "Anonymous",
        //   dob: formattedDate,
        //   email: values.email,
        //   mobile: values.mobile,
        //   hrNameNew: values.hrNameNew
        // }, { headers });

        setStep(1);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error("Error saving candidate details:", error);
        setSnackbar({
          open: true,
          message: error.response?.data?.status?.value || error.response?.data?.message || "Failed to save candidate details.",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClearForm = () => {
    formik.setValues({
      userName: "",
      dob: "",
      email: "",
      mobile: "",
      hrNameSelect: "",
      hrNameNew: "",
      paragraph: formik.initialValues.paragraph
    });
  };

  const words = useMemo(() => formik.values.paragraph.split(/\s+/), [formik.values.paragraph]);
  const progress = ((highlightedIndex + 1) / words.length) * 100;

  const sentences = useMemo(() => {
    const sents = [];
    let currentSentence = [];
    words.forEach((word, index) => {
      currentSentence.push({ word, globalIndex: index });
      if (/[.!?]$/.test(word) || index === words.length - 1) {
        sents.push(currentSentence);
        currentSentence = [];
      }
    });
    return sents;
  }, [words]);

  const startActualRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setStep(2);
      };


      mediaRecorderRef.current.start();
      setIsRecording(true);

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-US';

        let fullTranscriptBuffer = "";

        recognitionRef.current.onresult = (event) => {
          let interimTranscript = '';
          let currentFinal = '';

          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              currentFinal += event.results[i][0].transcript;
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }

          if (currentFinal) {
            fullTranscriptBuffer += currentFinal + " ";
          }

          const combinedTranscript = (fullTranscriptBuffer + interimTranscript).trim();
          const spokenWordsCount = combinedTranscript.split(/\s+/).filter(Boolean).length;

          setHighlightedIndex(spokenWordsCount > 0 ? spokenWordsCount - 1 : -1);
        };

        recognitionRef.current.onerror = (e) => console.error("Speech recognition error", e);
        recognitionRef.current.start();
      } else {
        console.warn("Speech recognition not supported in this browser.");
      }

    } catch (err) {
      console.error("Error accessing microphone:", err);
      setSnackbar({
        open: true,
        message: "Could not access microphone. Please check permissions.",
        severity: "error",
      });
      setStep(0);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const handleRestart = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null; // Prevent going to step 2
      try { mediaRecorderRef.current.stop(); } catch (e) { }
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { }
    }
    setIsRecording(false);
    setIsPaused(false);
    setHighlightedIndex(-1);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const togglePause = () => {
    if (!mediaRecorderRef.current) return;

    if (isPaused) {
      mediaRecorderRef.current.resume();
      if (recognitionRef.current) {
        recognitionRef.current.start();
      }
      setIsPaused(false);
    } else {
      mediaRecorderRef.current.pause();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsPaused(true);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Auto-scroll logic
  useEffect(() => {
    if (step === 1 && highlightedIndex >= 0 && wordRefs.current[highlightedIndex]) {
      wordRefs.current[highlightedIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightedIndex, step]);

  useEffect(() => {
    const fetchHrs = async () => {
      try {
        const response = await axiosInstance.get(apiEndPointsConfig.hrList);
        if (response.data?.data) {
          if (Array.isArray(response.data.data)) {
            // Backward compatibility
            setHrs(response.data.data);
          } else if (response.data.data.hrNames) {
            setHrs(response.data.data.hrNames);
            if (response.data.data.paragraphText) {
              formik.setFieldValue("paragraph", response.data.data.paragraphText);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching HR list:", error);
      }
    };
    fetchHrs();
  }, []);

  const uploadRecording = async () => {
    setIsUploading(true);
    const token = localStorage.getItem("token");
    const { userName, dob, email, mobile, hrNameSelect, hrNameNew, paragraph } = formik.values;

    const formattedDate = dob ? new Date(dob).toISOString().split('T')[0] : "";
    const fileName = `${(userName || "anonymous").replace(/\s+/g, '_')}_${formattedDate || "undated"}_reading.wav`;

    const file = new File([audioBlob], fileName, { type: "audio/wav" });
    const formData = new FormData();
    formData.append("files", file);
    formData.append("userName", userName || "Anonymous");
    formData.append("dob", formattedDate);
    formData.append("email", email);
    formData.append("mobile", mobile);
    formData.append("hrNameSelect", hrNameSelect);
    formData.append("hrNameNew", hrNameNew);
    formData.append("paragraph", paragraph);

    const headers = {
      "Content-Type": "multipart/form-data",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      // Upload recording
      await axiosInstance.post(apiEndPointsConfig.publicUpload, formData, { headers });
      setSnackbar({
        open: true,
        message: "Thank you for submitting your assessment",
        severity: "success",
      });
      setTimeout(() => {
        handleClearForm();
        setStep(0);
        setAudioBlob(null);
        setAudioUrl(null);
      }, 2500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.status?.value || "Something went wrong during upload.",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };


  return (
    <ParentWrapper>
      <Box sx={{ mb: 2, position: "relative" }}>
        {/* <Header
          title={"Voice Analysis Lab"}
          subHeading={"Advanced acoustic capturing for deep communication insights"}
        /> */}
        {localStorage.getItem("token") && (
          <Button
            startIcon={<ChevronLeft size={20} />}
            onClick={() => navigate(-1)}
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              '&:hover': { bgcolor: 'transparent', color: primary }
            }}
          >
            Back
          </Button>
        )}
      </Box>

      <Box>
        {step === 0 && (
          <Grow in={true}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 5,
                border: "1px solid rgba(99, 102, 241, 0.08)",
                backgroundColor: "#fff",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.04)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: { xs: 2, sm: 0 } }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#1F2937", letterSpacing: "-0.5px", textAlign: "center", fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
                  Hr assessment session
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 0 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: `${primary}15` }}>
                    <Sparkles size={24} color={primary} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#1F2937", letterSpacing: "-0.5px", fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
                    Prepare Session
                  </Typography>
                </Box>
                <Button
                  startIcon={<Trash2 size={16} />}
                  onClick={handleClearForm}
                  color="error"
                  sx={{ textTransform: "none", fontWeight: 600, width: { xs: "100%", sm: "auto" } }}
                >
                  Clear All
                </Button>
              </Box>

              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="userName"
                      name="userName"
                      label="Candidate Name"
                      placeholder="e.g. John Doe"
                      variant="outlined"
                      value={formik.values.userName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.userName && Boolean(formik.errors.userName)}
                      helperText={formik.touched.userName && formik.errors.userName}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "#F9FAFB" } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="dob"
                      name="dob"
                      label="Date of Birth"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      value={formik.values.dob}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.dob && Boolean(formik.errors.dob)}
                      helperText={formik.touched.dob && formik.errors.dob}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "#F9FAFB" } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      label="Email Address"
                      placeholder="candidate@example.com"
                      variant="outlined"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "#F9FAFB" } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="mobile"
                      name="mobile"
                      label="Mobile Number"
                      placeholder="10-digit number"
                      variant="outlined"
                      value={formik.values.mobile}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                      helperText={formik.touched.mobile && formik.errors.mobile}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "#F9FAFB" } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      id="hrNameSelect"
                      name="hrNameSelect"
                      // label="Select HR"
                      value={formik.values.hrNameSelect}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.hrNameSelect && Boolean(formik.errors.hrNameSelect)}
                      helperText={formik.touched.hrNameSelect && formik.errors.hrNameSelect}
                      SelectProps={{ native: true }}
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "#F9FAFB" } }}
                    >
                      <option value="">-- Choose HR --</option>
                      {hrs.map((hr) => (
                        <option key={hr} value={hr}>{hr}</option>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="hrNameNew"
                      name="hrNameNew"
                      label="Add HR Name (New)"
                      placeholder="Enter HR name if not in list"
                      value={formik.values.hrNameNew}
                      onChange={formik.handleChange}
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "#F9FAFB" } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      id="paragraph"
                      name="paragraph"
                      label="Script to Analyze"
                      value={formik.values.paragraph}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: "rgba(0,0,0,0.02)" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={formik.isSubmitting}
                      sx={{
                        background: `linear-gradient(135deg, ${primary} 0%, #4F46E5 100%)`,
                        px: { xs: 4, md: 8 },
                        py: 2.2,
                        width: { xs: "100%", sm: "auto" },
                        borderRadius: "50px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        boxShadow: `0 8px 25px ${primary}44`,
                        '&:hover': { transform: "translateY(-3px)", boxShadow: `0 12px 30px ${primary}66` },
                        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                      }}
                    >
                      {formik.isSubmitting ? <CircularProgress size={24} color="inherit" /> : "submit"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grow>
        )}

        {step === 1 && (
          <Fade in={true}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 8 },
                borderRadius: 6,
                textAlign: "center",
                background: "linear-gradient(145deg, #111827 0%, #1F2937 100%)",
                color: "#fff",
                minHeight: "500px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                position: "relative",
                boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
              }}
            >
              <Stack spacing={6}>
                <Box sx={{
                  // backgroundColor: "red",
                  display: "flex",
                  // alignItems: "center",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 3, sm: 0 },
                  width: "100%",
                  // maxWidth: "900px",
                  // mx: "auto",
                  // mb: 4,
                  // bgcolor: "rgba(255,255,255,0.03)",
                  p: 2,
                  borderRadius: 4
                }}>
                  <Box sx={{ textAlign: { xs: "center", sm: "left" }, flex: 1, mr: { xs: 0, sm: 6 } }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", textTransform: "uppercase", fontWeight: 800, letterSpacing: 1.5, display: "block" }}>
                      Read the Paragraph
                    </Typography>
                    {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 10,
                            flex: 1,
                            borderRadius: 5,
                            bgcolor: "rgba(255,255,255,0.1)",
                            '& .MuiLinearProgress-bar': {
                              background: `linear-gradient(90deg, ${primary} 0%, #4F46E5 100%)`,
                              borderRadius: 5
                            }
                          }}
                        />
                        <Typography sx={{ fontWeight: 900, minWidth: "50px", textAlign: "right", color: primary }}>
                          {Math.round(progress)}%
                        </Typography>
                      </Box> */}
                  </Box>

                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "center", sm: "flex-start" },
                    gap: 1.5,
                    bgcolor: !isRecording ? "rgba(16, 185, 129, 0.12)" : isPaused ? "rgba(234, 179, 8, 0.12)" : "rgba(239, 68, 68, 0.12)",
                    px: 3,
                    py: 1.5,
                    borderRadius: 4,
                    border: `1px solid ${!isRecording ? "rgba(16, 185, 129, 0.25)" : isPaused ? "rgba(234, 179, 8, 0.25)" : "rgba(239, 68, 68, 0.25)"}`,
                    boxShadow: `0 0 20px ${!isRecording ? "rgba(16, 185, 129, 0.1)" : isPaused ? "rgba(234, 179, 8, 0.1)" : "rgba(239, 68, 68, 0.1)"}`
                  }}>
                    <Box sx={{
                      width: 12, height: 12, borderRadius: "50%",
                      bgcolor: !isRecording ? "#10B981" : isPaused ? "#EAB308" : "#EF4444",
                      animation: !isRecording || isPaused ? "none" : "blink 1s infinite"
                    }} />
                    <Typography sx={{
                      fontWeight: 900,
                      color: !isRecording ? "#10B981" : isPaused ? "#EAB308" : "#EF4444",
                      fontSize: "0.85rem", letterSpacing: 1.5
                    }}>
                      {!isRecording ? "READY TO START" : isPaused ? "RECORDING PAUSED" : "CAPTURING LIVE"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{
                  textAlign: "center",
                  lineHeight: 1.4,
                  maxHeight: "300px",
                  overflowY: "auto",
                  px: 2,
                  '&::-webkit-scrollbar': { display: 'none' }
                }}>
                  <Box sx={{ textAlign: "center", lineHeight: 1.6 }}>
                    {sentences.map((sentence, sIdx) => {
                      const isSentenceActive = sentence.some(w => w.globalIndex === highlightedIndex);
                      return (
                        <span key={sIdx}>
                          {sentence.map((w) => {
                            let color = "rgba(255, 255, 255, 0.15)";
                            let fontWeight = 500;
                            let textShadow = "none";

                            if (w.globalIndex === highlightedIndex) {
                              color = "#FFFFFF";
                              // fontWeight = 900;
                            } else if (w.globalIndex < highlightedIndex) {
                              color = "rgba(255, 255, 255, 0.6)";
                              // fontWeight = 700;
                            } else if (isSentenceActive) {
                              color = "rgba(255, 255, 255, 0.4)";
                            }

                            return (
                              <Typography
                                key={w.globalIndex}
                                ref={el => wordRefs.current[w.globalIndex] = el}
                                component="span"
                                variant="h3"
                                sx={{
                                  display: "inline-block",
                                  mx: { xs: 0.5, md: 1.5 },
                                  my: 0.5,
                                  fontSize: { xs: "1.5rem", md: "3rem" },
                                  color,
                                  fontWeight,
                                  textShadow,
                                  transition: "color 0.2s ease"
                                }}
                              >
                                {w.word}
                              </Typography>
                            );
                          })}
                          <Box component="span" sx={{ mx: 2 }} />
                        </span>
                      );
                    })}
                  </Box>
                </Box>



                <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 2, md: 3 }, flexDirection: { xs: "column", sm: "row" }, width: "100%" }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<RefreshCcw size={18} />}
                    onClick={isRecording ? handleRestart : startActualRecording}
                    sx={{
                      px: { xs: 4, md: 6 },
                      py: 2.2,
                      width: { xs: "100%", sm: "auto" },
                      borderRadius: "50px",
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.3)",
                      '&:hover': { borderColor: "#fff", transform: "scale(1.05)", bgcolor: "rgba(255,255,255,0.1)" }
                    }}
                  >
                    Start
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={!isRecording}
                    startIcon={isPaused ? <Play size={18} /> : <Pause size={18} />}
                    onClick={togglePause}
                    sx={{
                      px: { xs: 4, md: 6 },
                      py: 2.2,
                      width: { xs: "100%", sm: "auto" },
                      borderRadius: "50px",
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.3)",
                      '&:hover': { borderColor: "#fff", transform: "scale(1.05)" },
                      '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    color="error"
                    disabled={!isRecording}
                    startIcon={<Square fill="currentColor" size={16} />}
                    onClick={stopRecording}
                    sx={{
                      px: { xs: 4, md: 8 },
                      py: 2.2,
                      width: { xs: "100%", sm: "auto" },
                      borderRadius: "50px",
                      fontSize: "1rem",
                      fontWeight: 600,
                      background: "linear-gradient(45deg, #EF4444 0%, #B91C1C 100%)",
                      boxShadow: "0 10px 30px rgba(239, 68, 68, 0.3)",
                      '&:hover': { transform: "scale(1.05)", boxShadow: "0 15px 40px rgba(239, 68, 68, 0.4)" },
                      '&.Mui-disabled': { background: 'rgba(239, 68, 68, 0.3)', color: 'rgba(255,255,255,0.5)', boxShadow: 'none' }
                    }}
                  >
                    End Recording
                  </Button>
                </Box>
              </Stack>
              <style>{`
                @keyframes blink { 0%, 100% { opacity: 1 } 50% { opacity: 0 } }
                @keyframes float { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-15px) } }
                .animate-float { animation: float 3s ease-in-out infinite; }
                .pulse-container { position: relative; }
                .pulse-container::before {
                  content: '';
                  position: absolute;
                  top: 50%; left: 50%;
                  transform: translate(-50%, -50%);
                  width: 120px; height: 120px;
                  background: ${primary};
                  border-radius: 50%;
                  opacity: 0.15;
                  animation: pulse-ring 2s infinite;
                }
                @keyframes pulse-ring {
                  0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.5; }
                  100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
                }
              `}</style>
            </Paper>
          </Fade>
        )}

        {step === 2 && (
          <Fade in={true} timeout={600}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 8 },
                borderRadius: 6,
                textAlign: "center",
                backgroundColor: "#ffffff",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: "0 20px 40px -10px rgba(0,0,0,0.08)",
                maxWidth: "700px",
                mx: "auto",
                position: "relative",
                overflow: "hidden"
              }}
            >
              {/* Decorative background blob */}
              <Box sx={{
                position: 'absolute',
                top: -100, right: -100,
                width: 300, height: 300,
                background: `radial-gradient(circle, ${primary}15 0%, rgba(255,255,255,0) 70%)`,
                borderRadius: '50%',
                zIndex: 0
              }} />

              <Stack spacing={5} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>

                {/* Animated Check Icon */}
                <Box sx={{ position: "relative" }}>
                  <Box sx={{
                    position: "absolute",
                    top: -10, left: -10, right: -10, bottom: -10,
                    borderRadius: "50%",
                    background: "rgba(16, 185, 129, 0.2)",
                    animation: "pulse-ring-success 2s infinite"
                  }} />
                  <Box sx={{
                    width: 80, height: 80,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 10px 25px rgba(16, 185, 129, 0.4)",
                    animation: "float-success 3s ease-in-out infinite"
                  }}>
                    <CheckCircle2 size={40} color="#fff" />
                  </Box>
                </Box>

                <Box sx={{ textAlign: "center", width: "100%", px: { xs: 2, sm: 0 } }}>
                  <Typography variant="h3" sx={{
                    fontWeight: 900,
                    color: "#0F172A",
                    letterSpacing: "-0.5px",
                    mb: 1.5,
                    fontSize: { xs: "1.75rem", md: "3rem" }
                  }}>
                    Capture Complete
                  </Typography>
                  <Typography sx={{ color: "#64748B", fontSize: { xs: "0.95rem", md: "1.1rem" }, maxWidth: "400px", mx: "auto", lineHeight: 1.6 }}>
                    Your recording sounds great. Please review it below before submitting it.
                  </Typography>
                </Box>

                <Box sx={{
                  width: "100%",
                  bgcolor: "#F8FAFC",
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
                  display: "flex", flexDirection: "column", gap: 2,
                  position: "relative",
                  overflow: "hidden"
                }}>
                  <Box sx={{
                    position: "absolute", top: 0, left: 0, width: "4px", height: "100%",
                    background: `linear-gradient(to bottom, ${primary}, #818CF8)`
                  }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, px: 1 }}>
                    <Volume2 size={20} color="#64748B" />
                    <Typography sx={{ fontWeight: 700, color: "#475569", fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: 1 }}>
                      Audio Preview
                    </Typography>
                  </Box>
                  <audio
                    src={audioUrl}
                    controls
                    style={{ width: "100%", height: "44px", borderRadius: "8px", outline: "none" }}
                    onTimeUpdate={(e) => {
                      const time = e.target.currentTime;
                      const index = Math.floor(time * (wpm / 60));
                      setPlaybackHighlightedIndex(index);
                    }}
                    onEnded={() => setPlaybackHighlightedIndex(-1)}
                  />

                  {/* Playback Paragraph with Sentence & Word Highlighting */}
                  <Box sx={{
                    textAlign: "center",
                    lineHeight: 1.6,
                    maxHeight: "180px",
                    overflowY: "auto",
                    mt: 2,
                    p: 2,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    border: "1px solid #E2E8F0",
                    '&::-webkit-scrollbar': { display: 'none' }
                  }}>
                    {sentences.map((sentence, sIdx) => {
                      const isSentenceActive = sentence.some(w => w.globalIndex === playbackHighlightedIndex);
                      return (
                        <Typography key={sIdx} component="span" sx={{
                          display: "inline",
                          color: isSentenceActive ? "#0F172A" : "#94A3B8",
                          transition: "color 0.2s ease"
                        }}>
                          {sentence.map((w) => {
                            const isWordActive = w.globalIndex === playbackHighlightedIndex;
                            return (
                              <Typography key={w.globalIndex} component="span" sx={{
                                display: "inline",
                                fontWeight: isWordActive ? 800 : (isSentenceActive ? 600 : 400),
                                color: isWordActive ? primary : "inherit",
                                mr: 1,
                                transition: "all 0.1s ease"
                              }}>
                                {w.word}
                              </Typography>
                            );
                          })}
                          <Box component="span" sx={{ mr: 1 }} />
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ width: "100%", justifyContent: "center", pt: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<RefreshCcw size={18} />}
                    onClick={() => setStep(1)}
                    sx={{
                      borderRadius: "50px",
                      px: { xs: 3, md: 5 },
                      py: 1.8,
                      width: { xs: "100%", sm: "auto" },
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "#64748B",
                      borderColor: "#CBD5E1",
                      borderWidth: "2px",
                      '&:hover': {
                        borderColor: "#94A3B8",
                        bgcolor: "#F8FAFC",
                        color: "#475569",
                        borderWidth: "2px",
                        transform: "translateY(-2px)"
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                  >
                    Discard & Retry
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <Sparkles size={18} />}
                    disabled={isUploading}
                    onClick={uploadRecording}
                    sx={{
                      background: `linear-gradient(135deg, ${primary} 0%, #4F46E5 100%)`,
                      px: { xs: 3, md: 6 },
                      py: 1.8,
                      width: { xs: "100%", sm: "auto" },
                      borderRadius: "50px",
                      fontWeight: 800,
                      fontSize: "1rem",
                      textTransform: "none",
                      boxShadow: `0 10px 25px ${primary}40`,
                      '&:hover': {
                        transform: "translateY(-2px)",
                        boxShadow: `0 15px 35px ${primary}60`
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                  >
                    {isUploading ? "Submit..." : "Submit"}
                  </Button>
                </Stack>
              </Stack>
              <style>{`
                @keyframes pulse-ring-success {
                  0% { transform: scale(0.8); opacity: 0.5; }
                  100% { transform: scale(1.5); opacity: 0; }
                }
                @keyframes float-success { 
                  0%, 100% { transform: translateY(0px) } 
                  50% { transform: translateY(-10px) } 
                }
              `}</style>
            </Paper>
          </Fade>
        )}
      </Box>

      <AutohideSnackbar
        message={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
      />
    </ParentWrapper>
  );
};

export default VoiceCapture;
